---
title: Personas, bundles, and workflows
summary: The mental model — how a persona, its knowledge, and (optionally) its skills, tools, and workflows fit together.
visibility: authenticated
audience: author-admin
section: Concepts
order: 35
last_verified: 2026-06-19
owner: docs
---

# Personas, bundles, and workflows

A few concepts show up everywhere in this product. Here's how they relate.

## The persona

A **persona** is the configurable expert you build and people talk to. It owns:

- an **identity** — its name, description, and how it speaks;
- a **knowledge base** — the documents it answers from, selected by a *graph* (see
  [How RAG works here](/help/concepts/how-rag-works));
- a **presentation mode** — text chat, voice, or a video avatar.

You own the personas you create. You can keep one private, share it with specific
people, or (as an admin) make it public to everyone on the deployment.

## Beyond answering: skills, tools, and workflows

Answering questions from documents is the foundation. A persona can also *do* things —
take an order, look up a price, send a payment link — not just talk.

A persona's behavior is assembled from a **bundle** — a versioned recipe of what the
persona is and can do. A bundle brings together:

- **Blocks** — the layered instructions that shape the persona's identity and rules.
- **Skills** — reusable packs of instructions for a kind of task (for example, *place an
  order*). A skill declares the capabilities it needs, but doesn't wire them up itself.
- **Tools** — concrete actions the persona can take. A **capability** is the
  vendor-neutral contract for an action (like "create a payment link"); an **adapter** is
  the implementation that fulfills it; a **tool binding** connects a capability to an
  adapter and, when credentials are needed, to a **credential set** (a named, encrypted
  group of API keys).
- **Workflows** — guided, multi-step flows for tasks that need structure. A workflow is a
  graph of **nodes** (talk to the user, call a tool, branch, wait for an event, finish).
  It collects values into **slots** along the way, and an author can put **guardrails** —
  plain-language rules — on the steps where the persona talks.

So the chain reads: **persona → bundle → skills, tools (capability + adapter +
credential set), and workflows** — with the persona's knowledge coming from its **graph**
on the side.

## Where to go next

- [Connect a persona to its knowledge](/help/how-to/connect-knowledge-rag)
- [Make a persona answer only from its documents](/help/how-to/answer-only-from-documents)
- [Glossary](/help/reference/glossary) — precise definitions of every term above.
